/**
 * Pure serialization for log context objects.
 *
 * Why: console.error(message, contextObject) reaches PostHog's console-error
 * autocapture with every argument coerced via String(), so error-tracking
 * issue titles read "… [object Object]" and the real error text (e.g.
 * "TypeError: Failed to fetch") is unrecoverable from the issue
 * (2026-07-02 useNotifications investigation).
 *
 * Kept free of import.meta so it stays testable under ts-jest — logger.ts
 * itself reads import.meta.env at module scope and is moduleNameMapper-stubbed
 * in jest (verifyFlag.ts class).
 */

const MAX_CONTEXT_CHARS = 2000;

export function serializeLogContext(context?: Record<string, unknown>): string {
  if (context == null) return '';
  let serialized: string;
  try {
    serialized =
      JSON.stringify(context, (_key, value) =>
        value instanceof Error ? `${value.name}: ${value.message}` : value,
      ) ?? '';
  } catch {
    // Circular structures etc. — degrade to String() rather than throw in a log call.
    serialized = String(context);
  }
  if (serialized.length > MAX_CONTEXT_CHARS) {
    return `${serialized.slice(0, MAX_CONTEXT_CHARS)}…`;
  }
  return serialized;
}
